import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function clean(rut: string) {
  return typeof rut === 'string' ? rut.replace(/^0+|[^0-9kK]+/g, '').toUpperCase() : ''
}

export function formatChileanRut(rut: string) {
  const cleanedRut: string = clean(rut)
  if (cleanedRut.length < 2) return cleanedRut
  let result = cleanedRut.slice(-4, -1) + '-' + cleanedRut.slice(-1)
  for (let i = 4; i < cleanedRut.length; i += 3) {
    result = cleanedRut.slice(-3 - i, -i) + '.' + result
  }
  return result
}

const dvCalculator = (rut: number) => {
  let digitsToVerify: number = rut
  let position = 0
  let ponderatedSum = 1
  for (; digitsToVerify; digitsToVerify = Math.floor(digitsToVerify / 10)) {
    ponderatedSum = (ponderatedSum + (digitsToVerify % 10) * (9 - (position % 6))) % 11
    position += 1
  }
  return ponderatedSum ? ponderatedSum - 1 : 'k'
}

export const validateRut = (rut: string) => {
  const rutWithoutDots = formatChileanRut(rut).replace(/\./g, '')
  if (!/^[0-9]+-[0-9kK]{1}$/.test(rutWithoutDots)) {
    return false
  }
  const tmp = rutWithoutDots.split('-')
  let digv = tmp[1]
  const tempRut = parseInt(tmp[0], 10)
  if (digv === 'K') digv = 'k'
  return dvCalculator(tempRut).toLocaleString() === digv
}

export const download64File = (base64File: string, filename: string, mimeType: string) => {
  const link = document.createElement('a')
  link.href = 'data:' + mimeType + ';base64,' + base64File
  link.download = filename
  link.click()
}

export const abstractTypeToOptions = (types: AbstractType[]) => {
  return types
    .map((type) => ({
      label: type.name,
      value: type.naturalKey,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const convertFileToBase64 = (file: File) => {
  return new Promise<FileData>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64String = typeof reader.result === 'string' ? reader.result.split(',')[1] : ''
      const filename = file.name
      const value: FileData = { base64String, filename }
      resolve(value)
    }
    reader.onerror = (error) => reject(error)
    reader.readAsDataURL(file)
  })
}

export const convertFilesToBase64 = (files: FileList) => {
  const filePromises: Promise<FileData>[] = Array.from(files).map((file) =>
    convertFileToBase64(file)
  )

  return Promise.all<FileData>(filePromises)
}

export function truncate(str: string, length: number) {
  if (!str) return 'Sin información'
  return str.length > length ? `${str.substring(0, length)}...` : str
}

export function formatRutIfMissingDV(rut: string): string {
  if (rut.includes('-')) return rut

  const cleanedRut = rut.replace(/\./g, '')

  if (!/^[0-9]+$/.test(cleanedRut)) {
    return rut
  }

  const computedDV = dvCalculator(parseInt(cleanedRut, 10))
  return `${cleanedRut}-${computedDV}`
}

export function splitTextIntoLines(
  text: string,
  maxWidth: number,
  ctx: CanvasRenderingContext2D
): string[] {
  if (ctx.measureText(text).width <= maxWidth) {
    return [text]
  }

  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = words[0]

  for (let i = 1; i < words.length; i++) {
    const testLine = `${currentLine} ${words[i]}`
    const testWidth = ctx.measureText(testLine).width

    if (testWidth <= maxWidth) {
      currentLine = testLine
    } else {
      lines.push(currentLine)
      currentLine = words[i]
    }
  }

  if (ctx.measureText(currentLine).width > maxWidth) {
    currentLine = currentLine.trim()
    while (ctx.measureText(currentLine + '...').width > maxWidth && currentLine.length > 1) {
      currentLine = currentLine.slice(0, -1)
    }
    currentLine = currentLine.replace(/\s+$/, '') + '...'
  }

  lines.push(currentLine)
  return lines
}

export const isPep = (politicamente_expuesto: object) =>
  Object.entries(politicamente_expuesto).filter(([, valor]) => valor === true).length > 0
